import { useState, useEffect } from "react";
import Axios from "axios";
import useDealershipsData from "./useDealershipsData";
import useConfiguratorData from "./useConfiguratorData";

/**
 * Hook para lógica de concesionarios
 * Maneja la selección por región, marca y concesionario preseleccionado
 */
const useDealershipLogic = (preselectedDealershipId = null) => {
    const {
        dealerships,
        dealershipsLoading,
        getDealerships,
        selectedDealership,
        setSelectedDealership,
        userPosition,
        setUserPosition,
    } = useDealershipsData();
    const { getPreselectedDealerId } = useConfiguratorData();

    // Estados para filtros y selecciones
    const [regions, setRegions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState({});
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState({});
    const [preselectedRegionValidated, setPreselectedRegionValidated] = useState(false);
    const [preselectedBrandValidated, setPreselectedBrandValidated] = useState(false);

    const preselectedDealerId = getPreselectedDealerId();
    const parsedPreselectedDealershipId = parseInt(preselectedDealershipId);

    // Inicialización: cargar concesionarios y regiones
    useEffect(() => {
        navigator.geolocation?.getCurrentPosition(
            ({ coords }) =>
                setUserPosition({ lat: coords.latitude, lng: coords.longitude }),
            () => {} // permiso denegado o error: se mantiene el orden default
        );
        getDealerships();
        Axios.get(`/api/dealerships/regions`)
            .then((response) => {
                setRegions(response.data);
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    useEffect(() => {
        const filteredSortedBrands = dealerships
            .filter((dealer) => dealer.region_id === selectedRegion.id)
            .filter((dealer) => dealer.brand_id !== null)
            .map((dealer) => ({
                id: dealer.brand_id,
                name: dealer.brand_name,
                rating: dealer.rating,
            }));
        setBrands(
            [...new Map(filteredSortedBrands.map((brand) => [brand["id"], brand])).values()].sort(
                (a, b) => (a.rating < b.rating ? 1 : -1)
            )
        );
    }, [selectedRegion]);

    useEffect(() => {
        if (parsedPreselectedDealershipId) return;

        if (
            preselectedDealerId &&
            dealerships.some((item) => item.brand_id === preselectedDealerId)
        ) {
            let dealership = dealerships.find((item) => item.brand_id === preselectedDealerId);
            let region = regions.find((region) => region.id === dealership.region_id);
            setSelectedRegion(region ? region : {});
            setPreselectedRegionValidated(true);
        }
    }, [dealerships, regions, brands, parsedPreselectedDealershipId]);

    useEffect(() => {
        if (parsedPreselectedDealershipId) return;

        if (
            preselectedDealerId &&
            dealerships.some((item) => item.brand_id === preselectedDealerId)
        ) {
            let dealership = dealerships.find((item) => item.brand_id === preselectedDealerId);
            let brand = brands.find((brand) => brand.id === dealership.brand_id);
            setSelectedBrand(brand ? brand : {});
            setPreselectedBrandValidated(true);
        }
    }, [brands, parsedPreselectedDealershipId]);

    useEffect(() => {
        if (!parsedPreselectedDealershipId || !dealerships.length || !regions.length) return;

        const dealership = dealerships.find((item) => item.id === parsedPreselectedDealershipId);

        if (!dealership) return;

        const region = regions.find((item) => item.id === dealership.region_id);
        const brand = {
            id: dealership.brand_id,
            name: dealership.brand_name,
            rating: dealership.rating,
        };

        setSelectedRegion(region || {});
        setSelectedBrand(brand);
        setSelectedDealership(dealership);
        setPreselectedRegionValidated(true);
        setPreselectedBrandValidated(true);
    }, [dealerships, regions, parsedPreselectedDealershipId]);

    const handleRegionInputChange = (e) => {
        setSelectedRegion(e);
        setSelectedBrand({});
        setSelectedDealership({});
    };

    const handleBrandInputChange = (e) => {
        setSelectedBrand(e);
        setSelectedDealership({});
    };

    const handleDealershipInputChange = (e) => {
        setSelectedDealership(e);
    };

    const dealershipFilter = () => {
        if (preselectedBrandValidated) {
            return dealerships.filter((item) => item.brand_id === selectedBrand.id);
        }
        return dealerships.filter(
            (item) => item.region_id === selectedRegion.id && item.brand_id === selectedBrand.id
        );
    };

    return {
        dealerships,
        dealershipsLoading,
        userPosition,
        regions,
        brands,
        selectedRegion,
        selectedBrand,
        selectedDealership,
        preselectedRegionValidated,
        preselectedBrandValidated,
        dealershipFilter,
        handleRegionInputChange,
        handleBrandInputChange,
        handleDealershipInputChange,
    };
};

export default useDealershipLogic;
